import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller for account services portfolio report.
 * Highlights active filter containers in real-time as users input values
 *
 * Usage:
 * <form data-controller="account-services-portfolio" data-account-services-portfolio-target="form">
 *   <div class="col">
 *     <label class="form-label">Filter Name</label>
 *     <input type="text" name="filter">
 *   </div>
 * </form>
 */
export default class extends Controller {
  static targets = ["form"]

  connect() {
    this.updateActiveFilters()
    this.bindRealtimeEvents()
  }

  disconnect() {
    this.unbindRealtimeEvents()
  }

  // Need this or the Download CSV button is triggered if user happens
  // to press enter in a non-select field (e.g. the range inputs).
  // Must bind to the form's "Download CSV"-type button if it exists.
  preventEnterSubmit(event) {
    if (event.key === 'Enter' && event.target.type !== 'textarea') {
      event.preventDefault();
    }
  }

  bindRealtimeEvents() {
    if (!this.hasFormTarget) return

    // Bind to all form inputs for real-time updates
    this.boundInputHandler = this.handleRealtimeInput.bind(this)
    this.boundChangeHandler = this.handleRealtimeChange.bind(this)

    this.formTarget.addEventListener('input', this.boundInputHandler)
    this.formTarget.addEventListener('change', this.boundChangeHandler)
  }

  unbindRealtimeEvents() {
    if (!this.hasFormTarget) return

    this.formTarget.removeEventListener('input', this.boundInputHandler)
    this.formTarget.removeEventListener('change', this.boundChangeHandler)
  }

  handleRealtimeInput(event) {
    // For text and number inputs, update immediately on input
    if (['INPUT'].includes(event.target.tagName)) {
      this.updateActiveFilters()
    }
  }

  handleRealtimeChange(event) {
    // For selects and other form elements, update on change
    this.updateActiveFilters()
  }

  formChanged() {
    this.updateActiveFilters()
  }

  updateActiveFilters() {
    if (!this.hasFormTarget) return

    // Clear all existing highlights
    this.clearHighlights()

    // Find all form groups (filter containers)
    const filterContainers = this.formTarget.querySelectorAll('.col')

    filterContainers.forEach(container => {
      if (this.hasActiveFilter(container)) {
        this.highlightContainer(container)
      }
    })
  }

  hasActiveFilter(container) {
    // Check for number inputs with values
    const numberInputs = container.querySelectorAll('input[type="number"]')
    for (const input of numberInputs) {
      if (input.value && input.value.trim() !== '') {
        return true
      }
    }

    // Check for text inputs with values
    const textInputs = container.querySelectorAll('input[type="text"]')
    for (const input of textInputs) {
      if (input.value && input.value.trim() !== '') {
        return true
      }
    }

    // Check for toggled checkboxes
    if (container.querySelector('input[type="checkbox"]:checked')) {
      return true
    }

    // Check for select dropdowns with non-default values
    const selects = container.querySelectorAll('select')
    for (const select of selects) {
      if (select.multiple) {
        // Multi-select: check if any options are selected
        const selected = Array.from(select.selectedOptions)
          .filter(opt => opt.value && opt.value !== '')
        if (selected.length > 0) {
          return true
        }
      } else {
        // Single select: check if selected value is not empty/default
        const selected = select.selectedOptions[0]
        if (selected && selected.value && selected.value !== '') {
          return true
        }
      }
    }

    return false
  }

  highlightContainer(container) {
    container.classList.add('filter-active')
    
    // Add highlight styling
    container.style.background = 'linear-gradient(135deg, rgba(13, 110, 253, 0.05) 0%, rgba(13, 110, 253, 0.1) 100%)'
    container.style.border = '1px solid rgba(13, 110, 253, 0.2)'
    container.style.borderRadius = '0.375rem'
    container.style.padding = '0.75rem'
    container.style.margin = '0.25rem 0'
    container.style.position = 'relative'
    container.style.transition = 'all 0.2s ease-in-out'

    // Add a subtle left border accent
    const accent = document.createElement('div')
    accent.className = 'filter-accent'
    accent.style.position = 'absolute'
    accent.style.left = '0'
    accent.style.top = '0'
    accent.style.bottom = '0'
    accent.style.width = '4px'
    accent.style.background = 'var(--bs-primary)'
    accent.style.borderTopLeftRadius = '0.375rem'
    accent.style.borderBottomLeftRadius = '0.375rem'
    
    container.style.position = 'relative'
    container.appendChild(accent)

    // Add icon indicator
    const indicator = document.createElement('div')
    indicator.className = 'filter-indicator'
    indicator.innerHTML = '<i class="fa-duotone fa-circle-check text-primary" style="font-size: 0.8rem;"></i>'
    indicator.style.position = 'absolute'
    indicator.style.top = '0.5rem'
    indicator.style.right = '0.5rem'
    indicator.style.zIndex = '10'
    
    container.appendChild(indicator)

    // Highlight the label
    const label = container.querySelector('.form-label, label')
    if (label) {
      label.style.color = 'var(--bs-primary)'
      label.style.fontWeight = '600'
    }
  }

  clearHighlights() {
    // Remove all active filter styling
    const activeContainers = this.formTarget.querySelectorAll('.filter-active')
    
    activeContainers.forEach(container => {
      container.classList.remove('filter-active')
      
      // Reset inline styles
      container.style.background = ''
      container.style.border = ''
      container.style.borderRadius = ''
      container.style.padding = ''
      container.style.margin = ''
      container.style.position = ''
      container.style.transition = ''

      // Remove accent and indicator elements
      const accent = container.querySelector('.filter-accent')
      const indicator = container.querySelector('.filter-indicator')
      
      if (accent) accent.remove()
      if (indicator) indicator.remove()

      // Reset label styling
      const label = container.querySelector('.form-label, label')
      if (label) {
        label.style.color = ''
        label.style.fontWeight = ''
      }
    })
  }

  // Method to manually trigger update (useful for Select2 integration)
  refreshHighlights() {
    this.updateActiveFilters()
  }
}
